import { createRoute, Link } from "@tanstack/react-router";
import { Bell, CheckCheck } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { NotificationCard } from "@/components/kb/notification-card";
import { PageHeader, SectionLabel } from "@/components/kb/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	useMarkAllNotificationsRead,
	useMarkNotificationRead,
	useNotifications,
} from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";
import { rootRoute } from "./__root";

const indexSearchSchema = z.object({
	notificationId: z.string().optional(),
});

type Group = "Today" | "Yesterday" | "Earlier";

function groupFor(value: string): Group {
	const date = new Date(value);

	if (Number.isNaN(date.getTime())) {
		return "Earlier";
	}

	const today = new Date();
	const yesterday = new Date();
	yesterday.setDate(today.getDate() - 1);

	if (date.toDateString() === today.toDateString()) {
		return "Today";
	}

	if (date.toDateString() === yesterday.toDateString()) {
		return "Yesterday";
	}

	return "Earlier";
}

function HomeComponent() {
	const { notificationId } = indexRoute.useSearch();
	const { data, isLoading, error } = useNotifications();
	const markRead = useMarkNotificationRead();
	const markAllRead = useMarkAllNotificationsRead();

	// biome-ignore lint/correctness/useExhaustiveDependencies: scroll again when the notification list arrives
	useEffect(() => {
		if (!notificationId) {
			return;
		}

		const frame = window.requestAnimationFrame(() => {
			document
				.getElementById(`notification-${notificationId}`)
				?.scrollIntoView({ behavior: "smooth", block: "center" });
		});

		return () => window.cancelAnimationFrame(frame);
	}, [notificationId, data]);

	if (isLoading) {
		return (
			<div className="flex flex-col gap-8">
				<PageHeader description="Loading your alerts." title="Notifications" />
				<div className="flex flex-col gap-3">
					<Skeleton className="h-28 w-full rounded-xl" />
					<Skeleton className="h-28 w-full rounded-xl" />
					<Skeleton className="h-28 w-full rounded-xl" />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col gap-8">
				<PageHeader title="Notifications" />
				<div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive">
					Couldn&rsquo;t load notifications: {error.message}
				</div>
			</div>
		);
	}

	const notifications = [...(data?.notifications ?? [])].sort(
		(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
	);
	const unreadCount = notifications.filter(
		(notification) => !notification.readAt,
	).length;
	const groups: Group[] = ["Today", "Yesterday", "Earlier"];

	return (
		<div className="flex flex-col gap-8">
			<PageHeader
				actions={
					unreadCount > 0 ? (
						<Button
							data-testid="mark-all-read"
							disabled={markAllRead.isPending}
							onClick={() =>
								markAllRead.mutate(undefined, {
									onError: (mutationError) =>
										toast.error("Could not mark notifications read", {
											description: mutationError.message,
										}),
								})
							}
							size="sm"
							variant="ghost"
						>
							<CheckCheck />
							Mark all read
						</Button>
					) : undefined
				}
				description={
					unreadCount > 0 ? `${unreadCount} unread` : "You’re all caught up."
				}
				title="Notifications"
			/>

			{notifications.length === 0 ? (
				<div className="flex flex-col items-center gap-4 rounded-xl border border-dashed px-6 py-16 text-center">
					<span className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
						<Bell className="size-5" />
					</span>
					<div className="flex flex-col gap-1">
						<p className="font-medium">No alerts yet.</p>
						<p className="text-sm text-muted-foreground">
							When one of your rules fires, it lands here.
						</p>
					</div>
					<Button asChild variant="outline">
						<Link to="/rules/create">New rule</Link>
					</Button>
				</div>
			) : (
				groups.map((group) => {
					const items = notifications.filter(
						(notification) => groupFor(notification.createdAt) === group,
					);

					if (items.length === 0) {
						return null;
					}

					return (
						<section className="flex flex-col gap-3" key={group}>
							<SectionLabel>{group}</SectionLabel>
							{items.map((notification) => (
								<div
									id={`notification-${notification.id}`}
									key={notification.id}
								>
									<NotificationCard
										className={cn(
											notificationId === notification.id &&
												"ring-2 ring-ring/60 ring-offset-2 ring-offset-background",
										)}
										markingRead={
											markRead.isPending &&
											markRead.variables === notification.id
										}
										notification={notification}
										onMarkRead={() =>
											markRead.mutate(notification.id, {
												onError: (mutationError) =>
													toast.error("Could not mark notification read", {
														description: mutationError.message,
													}),
											})
										}
									/>
								</div>
							))}
						</section>
					);
				})
			)}
		</div>
	);
}

export const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: HomeComponent,
	validateSearch: indexSearchSchema,
});
